import React from 'react';
import { useMutation } from '@tanstack/react-query';
import axios from './../../../../utils/oc-axios';
import { Group, GroupMutationsVariables, GroupMutationCallback } from '../Groups.types';
import useAlert, { AlertPriorityTypes } from '../../../../hooks/useAlert';
import { AlertDispatch } from '../../../../hooks/useAlert/types.useAlert';

const useCreateGroup = (cb?: GroupMutationCallback) => {
	const notification = useAlert()[1] as AlertDispatch;

	/**
     * Create a new group
     * 
     * @param {Group} group
     * @returns {Promise<void>}
     */
	const createGroup = React.useCallback(async (group: Group): Promise<void> => {
		return axios.post('/modules/groups', group)
			.then(res => res.data)
			.catch((error) => {
				throw new Error(error);
			});
	}, []);

	// Returns a mutation object in order to create a new group,
	// Optional: provide it with an alertId to use with notifications and closeModal method to close the modal
	// When provided they can be accessed in onSuccess or onError methods of the mutation object
	const mutation = useMutation((variables: GroupMutationsVariables) => {
		return createGroup(variables.group);
	}, {
		onSuccess(_data, variables) {
			if(variables.alertId) {
				notification('MODIFY', {
					alertID: variables.alertId,
					priority: AlertPriorityTypes.success,
					title: 'Skapa grupp',
					children: `${variables.group.name} har skapats`
				});
			}

			// Call the callback method provided to the hook if given
			if(cb) cb(variables.group);

			// Call the callback method from variables if such method is provided
			if(variables.cb) variables.cb();
		},
		onError(_error, variables) {
			if(variables.alertId) {
				notification('MODIFY', {
					alertID: variables.alertId,
					priority: AlertPriorityTypes.error,
					title: 'Skapa grupp',
					children: 'Det gick inte att skapa gruppen'
				});
			}
		}
	});

	return mutation;
};

export default useCreateGroup;